import { useFragment } from "@/generated";
import {
  DuplicateLessonDocument,
  type LessonFieldsFragment,
  LessonFieldsFragmentDoc,
  type SlideFieldsFragment,
  SlideFieldsFragmentDoc,
  SlidesByLessonIdDocument,
} from "@/generated/graphql";
import { getSlidesByLessonIdVars } from "@/lib/utils/graphql/getSlidesByLessonIdVars";
import { saveScrollTarget } from "@/lib/utils/scrollRestoration";
import { withToast } from "@/lib/utils/withToast";
import type { ApolloCache } from "@apollo/client";
import { useMutation } from "@apollo/client/react";
import { useNavigate } from "@tanstack/react-router";
import { updateDuplicateSlideCache } from "../slides/useDuplicateSlide";

/**
 * Hook for duplicating a lesson and all its child slides, updating the Apollo cache.
 *
 * @returns An object containing:
 *   - duplicateLesson: Async function to duplicate a lesson. Accepts the lesson ID, target parent ID,
 *     new name, and duplication type ("unique" | "linked"). Returns the duplicated lesson.
 *   - loading: Boolean indicating whether the mutation is in progress.
 *
 */

export const updateDuplicateLessonCache = (
  cache: ApolloCache,
  lesson: LessonFieldsFragment,
  slides: SlideFieldsFragment[],
  duplicationType: string | undefined
) => {
  for (const slide of slides) {
    updateDuplicateSlideCache(cache, slide, duplicationType);
  }

  // Create new slidesByLessonId query
  const activeSlides = slides.filter((slide) => !slide.isTrash);
  // The list holds main slides only; subslides hang off their parent, mirroring what the server
  // returns for this query.
  const mainSlides = activeSlides
    .filter((slide) => !slide.parentSlideId)
    .map((slide) => ({
      ...slide,
      children: activeSlides.filter((child) => child.parentSlideId === slide.id),
    }));

  cache.writeQuery({
    query: SlidesByLessonIdDocument,
    variables: getSlidesByLessonIdVars({ lessonId: lesson.id }),
    data: {
      slidesByLessonId: {
        hasMore: false,
        items: mainSlides,
        total: mainSlides.length,
      },
    },
  });

  if (!lesson.isTrash) {
    // Increment parent's itemCount
    cache.modify({
      id: `Project:${lesson.parentId}`,
      fields: {
        itemCount(existingData) {
          return existingData + 1;
        },
      },
    });

    // Add new duplicated lesson to parent query
    cache.modify({
      fields: {
        lessonsByParentId(existingData, { storeFieldName }) {
          if (!existingData.items) return;
          const key =
            lesson.parentId === null ? `"parentId":null` : `"parentId":"${lesson.parentId}"`;
          const isTrashKey = '"isTrash":false';

          if (!(storeFieldName.includes(key) && storeFieldName.includes(isTrashKey)))
            return existingData;

          const ref = { __ref: cache.identify(lesson) };

          return {
            ...existingData,
            items: [ref, ...existingData.items],
            total: (existingData.total || 0) + 1,
          };
        },
      },
    });
  }

  // Add to original lesson's linkedCopies
  if (duplicationType === "linked") {
    cache.modify({
      id: `Lesson:${lesson.originalLessonId}`,
      fields: {
        linkedCopies: (existingData) => {
          return [...existingData, { id: lesson.id, name: lesson.name }];
        },
      },
    });
  }
};

export function useDuplicateLesson() {
  const navigate = useNavigate();
  const [duplicateLessonMutation, { loading }] = useMutation(DuplicateLessonDocument, {
    update(cache, { data }, { variables }) {
      if (!data?.duplicateLesson) return;

      const duplicatedLesson = useFragment(LessonFieldsFragmentDoc, data.duplicateLesson.lesson);
      const slides = useFragment(SlideFieldsFragmentDoc, data.duplicateLesson.slides);

      cache.batch({
        update(cache) {
          updateDuplicateLessonCache(cache, duplicatedLesson, slides, variables?.duplicationType);
        },
      });
    },
  });

  const duplicateLesson = async (
    lessonId: string,
    targetParentId: string | null,
    name: string,
    duplicationType: "unique" | "linked",
    insertAfterSortOrder?: number | null
  ) => {
    return withToast(
      async () => {
        const result = await duplicateLessonMutation({
          variables: {
            lessonId,
            targetParentId,
            name,
            duplicationType,
            insertAfterSortOrder,
          },
        });

        const duplicatedLesson = result.data?.duplicateLesson.lesson
          ? useFragment(LessonFieldsFragmentDoc, result.data.duplicateLesson.lesson)
          : null;

        // Navigate and scroll to the duplicated lesson
        if (duplicatedLesson) {
          const pageKey = duplicatedLesson.parentId ?? "home";
          saveScrollTarget(`lesson-${duplicatedLesson.id}`, pageKey);

          if (duplicatedLesson.parentId) {
            navigate({
              to: "/admin/project/$projectId",
              params: { projectId: duplicatedLesson.parentId },
            });
          } else {
            navigate({ to: "/admin" });
          }
        }
      },
      {
        loading: "Duplicating lesson...",
        success: `Successfully duplicated lesson "${name}"`,
        error: "Failed to duplicate lesson",
      },
      "duplicateLesson"
    );
  };

  return {
    duplicateLesson,
    loading,
  };
}
